import React, { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { confirmFieldRedzones } from "services";

import { useCancelAsyncOnUnmount, useGetFieldRedzones, useTranslation } from "utils";

import { GET_FIELDS, GET_FIELD_REDZONES } from "consts";

import { FieldListField } from "models";

import { Button, Drawer, Typography, Message as message } from "components";

import { FieldSelectionList } from "pages/app/fields/components/FieldSelectionList";

type RedzoneConfirmationDrawerProps = {
  fields: FieldListField[];
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const RedzoneConfirmationDrawer = ({ fields, visible, onClose, onConfirm }: RedzoneConfirmationDrawerProps) => {
  const { t } = useTranslation("farm");
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  const { data: fieldRedzones } = useGetFieldRedzones({ enabled: visible });

  const signal = useCancelAsyncOnUnmount()();

  const hasFieldsToReview = fields.length > 0;

  const handleClose = () => {
    setSelectedFields([]);
    setLoading(false);
    onClose();
  };

  const handleRedzoneConfirmation = () => {
    setLoading(true);

    // the endpoint expects farm_field_redzone ids, not field ids
    const confirmedRedzoneIds = (fieldRedzones ?? []).filter(({ fieldId }) => selectedFields.includes(fieldId)).map(({ id }) => id);

    confirmFieldRedzones({
      ids: confirmedRedzoneIds,
      signal,
    })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: [GET_FIELDS] });
        queryClient.invalidateQueries({ queryKey: [GET_FIELD_REDZONES] });
        onConfirm();
      })
      .finally(() => {
        message({ content: t("redzoneDrawer.redzonesUpdated") });
        handleClose();
      });
  };

  return (
    <Drawer
      className="redzone-drawer"
      footer={
        hasFieldsToReview ? (
          <Button block disabled={loading} loading={loading} onClick={handleRedzoneConfirmation} type="primary">
            {t("redzoneDrawer.confirm")}
          </Button>
        ) : null
      }
      title={t("redzoneDrawer.title")}
      visible={visible}
      onClose={handleClose}
    >
      <Typography variant="paragraph-m" className="margin-bottom-200">
        {hasFieldsToReview ? t("redzoneDrawer.explainer") : t("redzoneDrawer.allFieldsReviewed")}
      </Typography>
      {hasFieldsToReview && (
        <FieldSelectionList fields={fields} isFieldSelectable={() => true} selectedFields={selectedFields} setSelectedFields={setSelectedFields} />
      )}
      <div className="margin-bottom-250" />
    </Drawer>
  );
};
